import React, { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { SearchInput, ButtonDisclosure, Dropdown } from "@toddle-edu/ds-web";
import { useI18n } from "Hooks";
import _ from "lodash";
import { useGradesContext } from "AppComponents/SharedEntities/Grades/context/GradesContext";
import { useCoursesContext } from "AppComponents/SharedEntities/Courses/context/CoursesContext";
import SchoolList from "AppComponents/SharedEntities/common/SchoolList";
import { useSelectParticipantsContext } from "../../context/SelectParticipantsContext";
import { ACTIVE_MODE } from "../../modules/SelectParticipantsConstants";
import classes from "./Toolbar.scss";

const Toolbar = ({ testId }) => {
  const { t } = useI18n(["calendar", "common"]);
  const {
    activeMode,
    searchText,
    setSearchText,
    schools,
    selectedSchoolIds,
    onSchoolChange,
    isAllSchoolsSelected,
    selectedSchoolLabel,
  } = useSelectParticipantsContext();

  const gradesCtx = useGradesContext();
  const coursesCtx = useCoursesContext();

  const isGradesMode = activeMode === ACTIVE_MODE.GRADES;

  const { allSchoolsCount, hierarchyLoading, selectedUserTypes } = isGradesMode
    ? gradesCtx
    : coursesCtx;

  const countsLoading = isGradesMode
    ? gradesCtx.allSchoolsCountLoading
    : coursesCtx.countsLoading;

  const showSchoolFilter = _.size(schools) > 1;

  const searchPlaceholder =
    activeMode === ACTIVE_MODE.MEMBERS
      ? t("calendar:search_members")
      : activeMode === ACTIVE_MODE.GRADES
      ? t("calendar:search_grades")
      : t("calendar:search_classes");

  const debouncedSetSearchText = useMemo(() => _.debounce(setSearchText, 300), [
    setSearchText,
  ]);

  const handleSearchChange = useCallback(
    value => {
      debouncedSetSearchText(value);
    },
    [debouncedSetSearchText]
  );

  const componentTestId = testId || "selectParticipantsModal";

  const schoolListOverlay = (
    <div className={classes.schoolFilterOverlay}>
      <SchoolList
        schools={schools}
        selectedSchoolIds={selectedSchoolIds}
        onSchoolChange={onSchoolChange}
        isAllSchoolsSelected={isAllSchoolsSelected}
        allSchoolsCount={allSchoolsCount}
        countsLoading={countsLoading}
        hierarchyLoading={hierarchyLoading}
        selectedUserTypes={selectedUserTypes}
        testId={componentTestId}
      />
    </div>
  );

  return (
    <div className={classes.toolbar}>
      <div className={classes.searchRow}>
        <SearchInput
          dsVersion="2.0"
          size="medium"
          placeholder={searchPlaceholder}
          value={searchText}
          onChange={handleSearchChange}
          testId={`${componentTestId}-search`}
        />
        {showSchoolFilter && (
          <Dropdown dsVersion="2.0" overlay={schoolListOverlay}>
            <ButtonDisclosure
              dsVersion="2.0"
              variant="neutral"
              type="outlined"
              size="medium"
              testId={`${componentTestId}-schoolFilter`}
            >
              <span className={classes.schoolFilterLabel}>
                {t("common:school")}: {selectedSchoolLabel}
              </span>
            </ButtonDisclosure>
          </Dropdown>
        )}
      </div>
    </div>
  );
};

Toolbar.propTypes = {
  testId: PropTypes.string,
};

Toolbar.defaultProps = {
  testId: undefined,
};

export default Toolbar;
